import React, { useState, useEffect, useContext, useRef } from 'react';
import { io } from 'socket.io-client';
import { Link } from 'react-router-dom';
import { AuthContext } from "../context/authContext";
import { Avatar } from "@mui/material";
import { Helmet } from 'react-helmet';
import './BrainDrain.css';

const BrainDrain = () => {
  const { currentUser } = useContext(AuthContext);
  const [socket, setSocket] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [winner, setWinner] = useState(null);
  const [message, setMessage] = useState('');
  const [isWaiting, setIsWaiting] = useState(true);
  const [nextQuestionAt, setNextQuestionAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const inputRef = useRef(null);
  const cdn = "https://files.geetsuhane.com/";

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(`${process.env.REACT_APP_API_END_POINT}/braindrain`, {
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('Brain Drain: Connected to socket');
    });

    newSocket.on('newQuestion', (data) => {
      console.log('Brain Drain: New question received', data);
      setQuestion(data);
      setIsWaiting(false);

      // If question is not yet answered, clear winner state
      if (!data.answered) {
        setWinner(null);
        setMessage('');
        setAnswer('');
        setNextQuestionAt(null);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    });

    newSocket.on('waiting', (data) => {
      setIsWaiting(true);
      setMessage(data.message);
      if (data.nextQuestionAt) {
        setNextQuestionAt(new Date(data.nextQuestionAt));
      }
    });

    newSocket.on('winner', (data) => {
      console.log('Brain Drain: Winner!', data);
      setWinner(data);
      if (data.nextQuestionAt) {
        setNextQuestionAt(new Date(data.nextQuestionAt));
      }
    });

    newSocket.on('answerResult', (data) => {
      if (!data.correct) {
        setMessage(data.message);
        setTimeout(() => setMessage(''), 2000);
      }
    });

    newSocket.on('leaderboard', (data) => {
      setLeaderboard(data);
    });

    newSocket.on('disconnect', () => {
      console.log('Brain Drain: Disconnected from socket');
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Countdown timer for next question
  useEffect(() => {
    if (!nextQuestionAt) {
      setTimeRemaining('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = nextQuestionAt - now;

      if (diff <= 0) {
        setTimeRemaining('Starting soon...');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextQuestionAt]);

  // Handle answer submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!answer.trim() || !question || !currentUser || question.answered) {
      return;
    }

    socket.emit('submitAnswer', {
      answer: answer.trim(),
      questionId: question.id,
      userId: currentUser.SID,
      userName: currentUser.name,
      userPic: currentUser.picture
    });

    setAnswer('');
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  // Check if question is answered (either locally or via flag)
  const isAnswered = winner || question?.answered;

  return (
    <div className="main-content-wrap">
      <Helmet>
        <title>Brain Drain - Funny Riddles Game | Geet Suhane</title>
        <meta name="description" content="Play Brain Drain - Answer funny riddles in Hindi and English. First correct answer wins! Free brain teasers and puzzles on Geet Suhane." />
        <meta name="keywords" content="brain drain game, riddles, Hindi riddles, puzzles, brain teasers, quiz game, fun games" />
        <meta property="og:title" content="Brain Drain - Funny Riddles Game" />
        <meta property="og:description" content="Test your brain! Answer funny riddles in Hindi and English. First correct answer wins!" />
        <meta property="og:type" content="game" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Brain Drain - Riddles Game" />
        <meta name="twitter:description" content="Test your brain with funny riddles! First correct answer wins!" />
        <link rel="canonical" href="https://geetsuhane.com/braindrain" />
      </Helmet>

      <div className="brain-drain-container">
        {/* Header */}
        <div className="brain-drain-header">
          <span className="brain-icon">&#129504;</span>
          <span className="brain-title">Brain Drain</span>
          <span className="brain-icon">&#129504;</span>
        </div>

        {/* Winner Overlay - shown persistently after answer */}
        {winner && (
          <div className="winner-overlay" style={{ position: 'relative', background: 'transparent' }}>
            <div className="winner-content">
              <div className="winner-celebration">&#127881; WINNER! &#127881;</div>
              <Avatar
                src={winner.userPic ? (winner.userPic.includes('googleusercontent') || winner.userPic.startsWith('http') ? winner.userPic : winner.userPic.startsWith('gsb/') ? cdn + winner.userPic : cdn + 'users/' + winner.userPic) : cdn + 'users/default-avatar.png'}
                alt={winner.userName}
                sx={{ width: 80, height: 80, margin: '10px auto' }}
              />
              <div className="winner-name">{winner.userName}</div>
              <div className="winner-answer">
                Answer: <strong>{winner.answer}</strong>
              </div>
              {timeRemaining && (
                <div className="new-question-countdown">
                  Next question in: {timeRemaining}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Question Area */}
        <div className="question-area">
          {isWaiting ? (
            <div className="waiting-message">
              <div className="loading-spinner"></div>
              <p>Waiting for next question...</p>
              {timeRemaining && (
                <p className="countdown-text">Next question in: {timeRemaining}</p>
              )}
            </div>
          ) : question ? (
            <>
              {question.category && (
                <div className="question-category">{question.category}</div>
              )}
              <div className="question-text question-en">
                {question.question_en}
              </div>
              <div className="question-text question-hi">
                {question.question_hi}
              </div>
              {isAnswered && !winner && (
                <div className="answered-notice">
                  <p>This question has been answered!</p>
                  {timeRemaining && (
                    <p className="countdown-text">Next question in: {timeRemaining}</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="waiting-message">
              <p>Loading question...</p>
            </div>
          )}
        </div>

        {/* Answer Input - only show if question is not answered */}
        {currentUser && !isAnswered && question && !question.answered && (
          <div className="answer-area">
            <form onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your answer..."
                className="answer-input"
                autoComplete="off"
              />
              <button type="submit" className="submit-btn">
                Submit
              </button>
            </form>
            {message && (
              <div className="answer-message wrong">{message}</div>
            )}
          </div>
        )}

        {/* Login prompt for guests */}
        {!currentUser && !isAnswered && (
          <div className="login-prompt">
            <Link to="/login">Login to play</Link>
          </div>
        )}

        {/* Leaderboard */}
        <div className="leaderboard-section">
          <div className="leaderboard-header">
            &#127942; Top Winners (24h)
          </div>
          <div className="leaderboard-list">
            {leaderboard.length > 0 ? (
              leaderboard.slice(0, 10).map((player, index) => (
                <div key={player.SID} className={`leaderboard-item ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : ''}`}>
                  <span className="rank">{index + 1}</span>
                  <Avatar
                    src={player.picture ? (player.picture.includes('googleusercontent') || player.picture.startsWith('http') ? player.picture : player.picture.startsWith('gsb/') ? cdn + player.picture : cdn + 'users/' + player.picture) : cdn + 'users/default-avatar.png'}
                    alt={player.name}
                    sx={{ width: 30, height: 30 }}
                  />
                  <span className="player-name">{player.name}</span>
                  <span className="wins">{player.wins} wins</span>
                </div>
              ))
            ) : (
              <div className="no-winners">No winners yet today</div>
            )}
          </div>
        </div>

        {/* Archive Link */}
        <div className="archive-link">
          <Link to="/braindrain/archive">View Past Questions</Link>
        </div>
      </div>
    </div>
  );
};

export default BrainDrain;
